import React from "react"
import { useState, useEffect, useMemo } from "react"
import { useAppContext } from "../contexts/AppContext"
import { TaskTypeSimple } from "../apis/quadriRestApi"
import QuadriRestApi from "../apis/quadriRestApi"
import { TaskType, TaskTypeProperty } from "../apis/quadriRestApi"
import { fetchFileItem, fetchFolderItems, fetchProjectRootId, fetchProject, ForbiddenError } from "../apis/connect"
import { TaskTypeDefinitionsCache } from "../apis/TaskTypeDefinitionsCache"
import { TaskTypeDefinition } from "../apis/quadriRestApi"
import { ModusButton, ModusDropdown, ModusList, ModusListItem } from "@trimble-oss/modus-react-components"

interface PathItem {
  id: string
  name: string
  versionId: string
}

export interface Item {
  id: string
  name: string
  versionId: string
  parentId: string
  path: PathItem[]
  type: "FILE" | "FOLDER"
}

interface PublishedTaskComponentProps {
  taskTypeSimple?: TaskTypeSimple
  projectLocation: string
}

enum ItemState {
  Uninitialized = "uninitialized",
  Loading = "loading",
  Loaded = "loaded",
  Error = "error",
  Unloaded = "unloaded"
}

enum ErrorText {
  ConnectFolderNotFound = "File not found",
  TrimBimFileNotFoundOnTask = "Trimbim file not found",
  ConnectFileNotFound = "File not found",
  NoAccessToFile = "No access",
  UnknownError = "Unknown error occurred"
}

const PublishedTaskComponent: React.FC<PublishedTaskComponentProps> = ({ taskTypeSimple, projectLocation }) => {
  const [showDropdown, setShowDropdown] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [detailedTaskType, setDetailedTaskType] = useState<TaskType | null>(null)
  const [trimbimFilename, setTrimbimFilename] = useState<string>("")
  const [trimbimFileGuid, setTrimbimFileGuid] = useState<string>("")
  const [isTrimbimFileLocal, setIsTrimbimFileLocal] = useState<boolean>(false)
  const [folderPathProperty, setFolderPathProperty] = useState<string>("")
  const [connectedFolderId, setConnectedFolderId] = useState<string>("")
  const [connectProjectName, setConnectProjectName] = useState<string>("")
  const [item, setItem] = useState<Item | null>(null)
  const [itemState, setItemState] = useState<ItemState>(ItemState.Uninitialized)
  const { loadedModels, selectedModels, accessToken, API } = useAppContext()
  const [errorText, setErrorText] = useState<ErrorText | null>(null)
  const [taskTypeDefinition, setTaskTypeDefinition] = useState<TaskTypeDefinition | null>(null)
  const [isSelected, setIsSelected] = useState(false)
  const [isFromExternalProject, setIsFromExternalProject] = useState(false)
  const [hasNoAccess, setHasNoAccess] = useState(false)
  const [hasNoAccessToProject, setHasNoAccessToProject] = useState(false)

  useEffect(() => {
    const initializeModelLoaded = async () => {
      const matchingModel = loadedModels.find((model: any) =>
        trimbimFileGuid ? model.id === trimbimFileGuid : model.name === trimbimFilename
      )
      if (matchingModel) {
        if (itemState !== ItemState.Loaded) {
          setItemState(ItemState.Loaded)
        }
      } else {
        if (
          itemState !== ItemState.Unloaded &&
          itemState !== ItemState.Loading &&
          itemState !== ItemState.Uninitialized
        ) {
          setItemState(ItemState.Unloaded)
        }
      }
    }

    if (trimbimFilename) initializeModelLoaded()
  }, [itemState, trimbimFilename, trimbimFileGuid, loadedModels])

  useEffect(() => {
    const initializeSelectedModel = async () => {
      const matchingModel = selectedModels.find((model: any) => model.modelId === item?.id)
      if (matchingModel) {
        setIsSelected(true)
      } else {
        setIsSelected(false)
      }
    }

    if (item) initializeSelectedModel()
  }, [item, selectedModels])

  useEffect(() => {
    const initializeTaskTypeDefinition = async () => {
      const taskTypeDefinition = TaskTypeDefinitionsCache.getInstance().getTaskTypeDefinition("PRESENT_AS_TRIMBIM_TOOL")
      setTaskTypeDefinition(taskTypeDefinition || null)
    }
    initializeTaskTypeDefinition()
  }, [accessToken, projectLocation])

  const findPropertyByName = (taskType: TaskType, name: string): TaskTypeProperty | undefined => {
    return taskType.details.properties.find((property) => property.definition?.name === name)
  }

  const isLocalFile = (filePath: string): boolean => {
    // Check if the file path starts with a drive letter (e.g., "C:\") or matches a local file pattern
    const localFilePattern = /^[a-zA-Z]:[\\/]/ // Matches "C:\", "D:\", "E:/", etc.
    return localFilePattern.test(filePath)
  }

  useEffect(() => {
    const fetchItemFromTask = async () => {
      if (!API) return
      if (!taskTypeSimple) return
      if (detailedTaskType) return

      const restApi = new QuadriRestApi()
      await restApi.initialize(accessToken, projectLocation)
      const projectID = (await API.project.getProject()).id
      const taskType = await restApi.getTask(projectID, taskTypeSimple.taskId)
      setDetailedTaskType(taskType)

      const trimbimFileGuid = findPropertyByName(taskType, "FILE_GUID")
      setTrimbimFileGuid(trimbimFileGuid?.value || "")
      const outputTrimBimFile = findPropertyByName(taskType, "OUTPUT_TRIMBIM_FILE")
      if (!outputTrimBimFile) {
        console.log("Property with name 'CONNECTED_FOLDER_ID_PATH' not found.")
        setErrorText(ErrorText.TrimBimFileNotFoundOnTask)
        setHasError(true)
        return
      }
      setTrimbimFilename(outputTrimBimFile?.value || "")
      setIsTrimbimFileLocal(isLocalFile(outputTrimBimFile?.value))

      const connectProjectName = findPropertyByName(taskType, "CONECTED_PROJECT")
      setConnectProjectName(connectProjectName?.value)

      const connectedFolderProperty = findPropertyByName(taskType, "CONNECTED_FOLDER_ID_PATH")
      if (!connectedFolderProperty) {
        console.log("Property with name 'CONNECTED_FOLDER_ID_PATH' not found.")
        setErrorText(ErrorText.ConnectFolderNotFound)
        setHasError(true)
        return
      }
      setConnectedFolderId(connectedFolderProperty.value)

      const folderPathProperty = findPropertyByName(taskType, "CONNECTED_FOLDER_PATH")
      if (folderPathProperty) {
        setFolderPathProperty(folderPathProperty.value)
      }

      const connectProjectId = connectedFolderProperty.value.split("\\")[0]
      if (connectProjectId !== projectID) {
        setIsFromExternalProject(true)
        try {
          await fetchProject(connectProjectId, accessToken, projectLocation)
        } catch (error) {
          if (error instanceof ForbiddenError) {
            setHasNoAccessToProject(true)
            setHasNoAccess(true)
            setErrorText(ErrorText.NoAccessToFile)
            setHasError(true)
          }
          return
        }
      }

      try {
        //If the file id is set use it otherwise search for the file by name in the folder
        let item: Item | null = null
        if (trimbimFileGuid) {
          item = await fetchFileItem(trimbimFileGuid.value, accessToken, projectLocation)
        }
        if (!item) {
          const folderId = connectedFolderProperty.value.includes("\\")
            ? connectedFolderProperty.value.split("\\").pop()
            : await fetchProjectRootId(projectID, accessToken, projectLocation) // Set folderId to "rootItem" if no backslash is found

          const folderItems = await fetchFolderItems(folderId, accessToken, projectLocation)
          item = folderItems.find((item: any) => item.name === outputTrimBimFile.value)
        }

        if (!item) {
          setErrorText(ErrorText.ConnectFileNotFound)
          setHasError(true)
          return
        }

        setItem(item)
      } catch (error) {
        console.error("Error fetching item:", error)
        if (error instanceof ForbiddenError) {
          setErrorText(ErrorText.NoAccessToFile)
          setHasNoAccess(true)
        } else {
          setErrorText(ErrorText.UnknownError)
        }
        setHasError(true)
      }
    }

    fetchItemFromTask()
  }, [taskTypeSimple, accessToken, API, projectLocation, detailedTaskType])

  const toggleModelVersionInViewer = async (shouldLoad: boolean) => {
    if (!API || !item) return

    try {
      setItemState(ItemState.Loading)
      await API.viewer.toggleModelVersion({ id: item.id, versionId: item.versionId }, shouldLoad, true)
      setItemState(shouldLoad ? ItemState.Loaded : ItemState.Unloaded)
      if (!shouldLoad) setHasError(false)
    } catch (error) {
      console.error(`Failed to ${shouldLoad ? "load" : "unload"} model:`, error)
      if (shouldLoad) setHasError(true)
    }
  }

  const onToggleModelButtonClick = async () => {
    await toggleModelVersionInViewer(!(itemState === ItemState.Loaded))
  }

  async function getObjects() {
    const allObjects = (await API?.viewer.getObjects()) ?? []
    return allObjects.filter((m) => m.modelId === item?.id).map((m) => ({ modelId: m.modelId, objects: m.objects }))
  }

  const onItemClicked = async () => {
    if (!item || itemState === ItemState.Uninitialized || itemState === ItemState.Loading) return
    setIsSelected(true)

    const objects = await getObjects()
    const objectRuntimeIds = objects.flatMap((obj) =>
      Array.isArray(obj.objects) && obj.objects.length > 0 ? obj.objects.map((o) => o.id) : []
    )
    try {
      await API?.viewer.setSelection({ modelObjectIds: [{ modelId: item.id, objectRuntimeIds }] }, "set")
    } catch (error) {
      console.error("Failed to set selection:", error)
    }
  }

  const iconClassName = useMemo(() => {
    if (hasError) return "tc-icon-eye-visibility-off-error error-icon"
    if (itemState === ItemState.Loaded) return "tc-icon-eye-visibility on-icon"
    if (itemState === ItemState.Loading) return "tc-icon-cancel"
    return "tc-icon-eye-visibility-off-download off-icon"
  }, [hasError, itemState])

  const getItemTitle = (): string | undefined => {
    if (!hasError) {
      const folderPath = item?.path?.map((folder) => folder.name).join("\\")
      return `${folderPath}\\${item?.name}`
    }

    if (isFromExternalProject) {
      if (hasNoAccessToProject) {
        return `The file is published to a Connect project where you have No access.`
      }
      return hasNoAccess
        ? `The file is published to project ${connectProjectName}, and is located in a folder where you have No access.`
        : `File ${trimbimFilename} not found in project ${connectProjectName}.`
    }

    if (hasNoAccess) {
      return `The file is published to a folder in this project where you have No access.`
    }

    if (trimbimFilename) {
      return isTrimbimFileLocal
        ? `File is published to local device in folder ${trimbimFilename}. It may be upload manually or with Connect Sync to a connect project, there is not enough information available to find the file.`
        : `The published file "${trimbimFilename}" could not be found.\nMake sure it is uploaded to Connect and the task is shared.`
    }
    return `The folder "${folderPathProperty}" could not be found in Connect`
  }

  const dropdownId = `dropdown-toggle-${taskTypeSimple?.taskId}`
  const fitToView = async () => {
    if (!item || itemState !== ItemState.Loaded) {
      toggleModelVersionInViewer(true)
      return
    }

    const objects = await getObjects()
    const objectRuntimeIds = objects.flatMap((obj) =>
      Array.isArray(obj.objects) && obj.objects.length > 0 ? obj.objects.map((o) => o.id) : []
    )
    try {
      API?.viewer.setCamera({
        modelObjectIds: [{ modelId: item.id, objectRuntimeIds }]
      })
    } catch (error) {
      console.error("Failed to set selection:", error)
    }
  }

  const showOnly = async () => {
    for (const model of loadedModels) {
      if (model.id !== item?.id) {
        try {
          await API?.viewer.toggleModelVersion({ id: model.id, versionId: model.versionId }, false, true)
        } catch (error) {
          console.error("Failed to unload model:", error)
        }
      }

      if (!loadedModels.some((m) => m.id === item?.id)) {
        await toggleModelVersionInViewer(true)
      }
    }
  }

  const seeDetails = async () => {
    if (!item || itemState !== ItemState.Loaded) {
      await toggleModelVersionInViewer(true)
    }

    if (item?.id) {
      try {
        const file = await API?.viewer.getLoadedModel(item?.id)
        // @ts-expect-error
        API?.viewer.showModelDetails(file)
      } catch (error) {
        console.error("Failed to show model details:", error)
      }
    }
  }

  return (
    <div
      className={`panel-single-item ${isSelected ? "selected" : ""}`}
      onClick={(e) => {
        // Prevent opening the dropdown from selecting the item
        if ((e.target as HTMLElement).id === dropdownId) {
          return
        }
        onItemClicked()
      }}
      title={getItemTitle()}
    >
      <div className="item-content-flex">
        <button
          className="button icon-medium icon-circle tertiary"
          onClick={() => onToggleModelButtonClick()}
          aria-label="Toggle model visibility"
        >
          {itemState === ItemState.Loading && <span className="item-content-loading"></span>}
          <div className="icon-container">
            <i className={`icon-eye ${iconClassName}`}></i>
            <img
              src={`data:image/svg+xml;utf8,${encodeURIComponent(taskTypeDefinition?.iconSvg || "")}`}
              alt=""
              className={`item-icon`}
            />
            {isFromExternalProject && <div className="external-project-indicator">XRef</div>}
          </div>
        </button>
        <div className="item-text-container">
          <span className={`item-name ${hasError ? "error-text" : ""}`}>{taskTypeSimple?.name}</span>
          <span className="error-text">{errorText}</span>
        </div>
        <div style={{ position: "relative" }}>
          <ModusDropdown
            toggleElementId={dropdownId}
            disabled={errorText !== null}
            customPlacement={{ right: 1 }}
            showDropdownListBorder={false}
          >
            <ModusButton
              id={dropdownId}
              slot="dropdownToggle"
              showCaret={false}
              iconOnly="more_vertical"
              buttonStyle="borderless"
              color="tertiary"
              size="medium"
              aria-label="model dropdown"
            ></ModusButton>
            <div
              slot="dropdownList"
              style={{
                margin: "4px", // Add margin so shadow isn't clipped
                boxShadow: "0 1px 4px rgba(0, 0, 0, 0.2)",
                borderRadius: "2px",
                backgroundColor: "white"
              }}
            >
              <ModusList slot="dropdownList" className="dropdown-list-items">
                <ModusListItem borderless={true} onClick={fitToView} disabled={itemState !== ItemState.Loaded}>
                  Fit to view
                </ModusListItem>
                <ModusListItem borderless={true} onClick={showOnly}>
                  Show only
                </ModusListItem>
                <ModusListItem borderless={true} onClick={seeDetails}>
                  See details
                </ModusListItem>
              </ModusList>
            </div>
          </ModusDropdown>
        </div>
      </div>
    </div>
  )
}

export default PublishedTaskComponent
